import React, {useContext, useEffect, useState} from 'react';
import {Button, Grid, TextField, TextFieldProps, Typography} from '@mui/material';
import {IMaskMixin, useIMask} from 'react-imask';
import {toString} from 'lodash-es';
import {AutoField, SelectOption} from '../../../components/AutoForm/AutoFieldNew';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {AccountContext} from '../../../contexts/AccountContext';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {
  withdrawalAccountNameField,
  withdrawalAccountNumberField,
  withdrawalAccountStateField,
  withdrawalProductProviderField,
} from './withdrawalFields';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OVRecommendActionEnum, BankDetailsEnum} from '../../../schema-types/globalTypes';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import OWNERSHIP from '../../../constants/Ownership';
import {PlanContactField} from '../RecommendsSetUpNew/PlanContactField';
import {Panel} from '../../../components/Panel/Panel';
import {
  FormReadOnly,
  FormReadOnlyContext,
} from '../../../components/AutoForm/AutoFormNew';
import {
  useAutoForm,
  useAutoField,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {SaltusAny} from '../../../utils/SaltusAny';
import {sortAlphabeticallyByName} from '../../bulkAdmin/useBulkAdmin';
import {PlanInterestedPartyField} from '../RecommendsSetUpNew/PlanInterestedPartyField';
import validateUUID from '../../../utils/validateUUID';
import {UPDATES} from '../../../shared/UPDATES';
import {loadNewCurrentAccount} from '../hooks/withdrawalHooks';

export {loadNewCurrentAccount};

export const NewCurrentAccountDetails = () => {
  const {accountId, account} = useContext(AccountContext);
  const {recommendDetails, isSPPDA} = useRecommendContext();
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  // const {accountData} = useAccountData();
  const {rawProviders, queryGetProductProviders} = usePlanNamesProviders();
  useEffect(() => {
    if (!rawProviders) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
        },
      });
    }
  }, [rawProviders]);

  useEffect(() => {
    loadNewCurrentAccount({
      recommendDetails,
      setFieldValue,
    });
  }, [recommendDetails]);

  return (
    <>
      <ScrollableModal
        size="small"
        open={showNewProviderModal}
        onClose={() => setShowNewProviderModal(false)}
      >
        <Panel title="Request new provider">
          <Typography>
            Please contact our support desk if you require an additional provider to be
            added:{' '}
          </Typography>
          <Button
            style={{textTransform: 'lowercase'}}
            href="mailto:data@saltus.co.uk"
            type="button"
            variant="text"
          >
            data@saltus.co.uk
          </Button>
        </Panel>
      </ScrollableModal>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField field={withdrawalAccountStateField} />
        </Grid>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...withdrawalProductProviderField,

              options:
                rawProviders?.productProviders
                  ?.filter((x) => x.isBank)
                  .sort(sortAlphabeticallyByName)
                  .map<SelectOption>((x) => ({label: x.name!, value: x.id})) || [],
            }}
          />
          <Button
            onClick={() => setShowNewProviderModal(true)}
            type="button"
            variant="text"
            size="small"
            style={{marginBottom: 0}}
          >
            Request new provider
          </Button>
        </Grid>
        <Grid item xs={6}>
          <AutoField field={{...withdrawalAccountNameField}} />
        </Grid>
        <Grid item xs={6}>
          {account?.isTrust ? (
            <PlanInterestedPartyField
              label="Interested Party"
              formikKeyPrefix="withdraw.destination"
            />
          ) : (
            <PlanContactField label="Ownership" formikKeyPrefix="withdraw.destination" />
          )}
        </Grid>

        <FormReadOnly
          readOnly={
            values.withdraw?.destination?.bankDetails === BankDetailsEnum.NOT_KNOWN
          }
        >
          <Grid item xs={6}>
            <AutoField
              disabled={
                values.withdraw?.destination?.bankDetails === BankDetailsEnum.NOT_KNOWN
              }
              field={withdrawalAccountNumberField}
            />
          </Grid>

          <Grid className="sentry-mask" item xs={6}>
            <SortCodeField />
          </Grid>
        </FormReadOnly>
      </Grid>
    </>
  );
};

const SortCodeInternalMaskTextField = IMaskMixin((props) => (
  <TextField
    fullWidth
    label="Sort code"
    variant="outlined"
    className="sentry-mask"
    size="small"
    InputLabelProps={{shrink: true}}
    {...(props as any)}
  />
));

const SortCodeField = React.forwardRef<HTMLElement>(function TextMaskCustom(props, ref) {
  const [sortCodeField, meta, {setValue: setSortCode}] = useAutoField<string | undefined>(
    'withdraw.destination.sortCode'
  );
  const {readOnly: readOnlyContext} = useContext(FormReadOnlyContext);
  return (
    <SortCodeInternalMaskTextField
      // {...other}
      // @ts-ignore
      error={meta.error}
      value={sortCodeField.value || ''}
      mask="00-00-00"
      //   mask={[/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/]}
      definitions={{
        '#': /[1-9]/,
      }}
      disabled={readOnlyContext}
      inputRef={ref as any}
      onAccept={(value: SaltusAny) => setSortCode(UPDATES._359, value)}
      overwrite
    />
  );
});
